import React from 'react';
import { Flex, Tabs, TabsProps, Tooltip } from 'antd';
import { css } from 'styled-components';

import { useDevExTheme } from '../common/themes/DevExTheme';

import { DevExBadge } from './DevExBadge';
import { DevExSkeleton } from './DevExSkeleton';

const styles = {
  tabs: css`
    color: inherit;
    line-height: inherit;

    .ant-tabs-nav {
      ::before {
        border-color: ${(props) => props.theme.color.border.primary};
      }

      .ant-tabs-ink-bar {
        background-color: ${(props) => props.theme.color.brand.default};
      }

      .ant-tabs-tab {
        font-size: ${(props) => props.theme.variable.fontSize.md};

        .ant-tabs-tab-btn {
          color: ${(props) => props.theme.color.text.secondary};
          text-shadow: none;
        }

        &.ant-tabs-tab-active {
          .ant-tabs-tab-btn {
            color: ${(props) => props.theme.color.text.primary};
          }
        }

        &.ant-tabs-tab-disabled {
          // If modifying pointer-events property here be careful the disabled + tooltip case still works

          .ant-tabs-tab-btn {
            color: ${(props) => props.theme.color.text.disabled};
          }
        }
      }
    }
  `,
};

// couldn't import this type directly from AntD, so I apologize for this mess - @nathan
type Tab = Required<Pick<TabsProps, 'items'>>['items'][number];

interface DevExTab extends Tab {
  count?: number;
  disabled?: boolean;
  tooltipText?: string;
}

interface DevExTabsProps extends TabsProps {
  items?: DevExTab[];
  loading?: boolean;
}

export const DevExTabs: React.FC<DevExTabsProps> = (props) => {
  const { items, loading, ...passthroughProps } = props;

  const theme = useDevExTheme();

  const renderItem = (item: DevExTab) => {
    if (loading) {
      return <DevExSkeleton width={80} />;
    }

    // if item.count is provided, add a DevExBadge beside item.label
    if (item.count !== undefined) {
      return (
        <Flex gap={theme.variable.spacing.xs}>
          {item.label}
          <DevExBadge count={item.count} active={props.activeKey === item.key} />
        </Flex>
      );
    }
    if (item.tooltipText) {
      return <Tooltip title={item.tooltipText}>{item.label}</Tooltip>;
    }

    return item.label;
  };
  return (
    <Tabs
      css={styles.tabs}
      items={items?.map((item) => ({
        ...item,
        label: renderItem(item),
        disabled: loading || item.disabled,
      }))}
      {...passthroughProps}
    />
  );
};
